import { useState } from 'react'
import type { MouseEvent } from 'react'
import { useCurrentUser } from 'context'
import { Group, Button, Tooltip, NavigationButton } from 'components'
import { IconClipboardCheck, IconFileText, IconUpload } from '@tabler/icons-react'
import { LessonJournalButtonsProps } from './interfaces'
import { useArcTranslation, useFeatureToggle } from 'hooks'
import { TaskTrackerAssessmentDrawer } from 'pages/TaskTracker/assessmentDrawer'
import { TeacherAssessmentDrawer } from 'pages/TaskTracker/assessmentDrawer/teacherAssessmentDrawer'
import { StudentDeliverableDrawer } from 'pages/TaskTracker/deliverableDrawer'

export const LessonJournalButtons = ({
  projectStatus,
  projectKind,
  lessonID,
  lesson,
  getJournalURL,
  getReportURL,
  canFillEditReport,
}: LessonJournalButtonsProps) => {
  const { t } = useArcTranslation()
  const { currentUser } = useCurrentUser()
  const isArc = useFeatureToggle('arc-mosaico-project') as boolean
  const [studentDrawerOpened, setStudentDrawerOpened] = useState(false)
  const [teacherDrawerOpened, setTeacherDrawerOpened] = useState(false)
  const [deliverableDrawerOpened, setDeliverableDrawerOpened] = useState(false)

  // Don't show journal buttons for templates
  if (projectKind === 'template') {
    return null
  }

  const profile = currentUser?.profile_used
  const isStudent = profile === 'student'
  const isTutorOrManager = profile === 'tutor' || profile === 'manager'

  // Arc hides the assessment button when the lesson has no assessment activity
  // and no deliverable activity. Fall back to showing it if lesson isn't passed.
  const lessonHasAssessableActivity =
    lesson?.sessionMeetingActivities?.some(a => a.hasAssessment === true || a.hasTasks === true) ?? true
  const showAssessmentButton = !isArc || lessonHasAssessableActivity

  // Arc (AR-872): teachers can't assess a lesson that only has deliverable
  // activities — show the button disabled with an explanatory tooltip.
  const lessonHasAssessment =
    lesson?.sessionMeetingActivities?.some(a => a.hasAssessment === true) ?? true
  const disableAssessmentButton = isArc && isTutorOrManager && !lessonHasAssessment

  const assessmentLabel = isArc
    ? isStudent
      ? t('new_project_view.button_self_assess')
      : t('new_project_view.button_assess_students')
    : t('general_buttons_fill_journal')

  // Arc routing:
  //   - student: inline self-assessment drawer (single student)
  //   - teacher: new multi-student Teacher Assessment Drawer (AR-539)
  //   - legacy:  link to /sessions/:id/journal
  const openStudentDrawer = isArc && isStudent
  const openTeacherDrawer = isArc && isTutorOrManager

  // Student-only "Submit deliverable" button: lists the lesson's deliverable
  // activities in a single drawer (mirrors the Arc prototype).
  const deliverableActivityIds =
    lesson?.sessionMeetingActivities?.filter(a => a.hasTasks === true).map(a => a.id) ?? []
  const showDeliverableButton = isArc && isStudent && deliverableActivityIds.length > 0

  // Arc (AR-828): teachers don't get the report button at all, and students
  // see it renamed to "View lesson assessments".
  const showReportButton = !(isArc && isTutorOrManager)

  const reportButtonLabel = isTutorOrManager
    ? t('general_view_student_report')
    : isArc
      ? t('general_view_lesson_assessments')
      : t('general_view_teacher_report')

  const handleAssessmentClick = () => {
    if (openStudentDrawer) setStudentDrawerOpened(true)
    else if (openTeacherDrawer) setTeacherDrawerOpened(true)
  }

  const opensInDrawer = openStudentDrawer || openTeacherDrawer

  return (
    <>
      <Group spacing='sm'>
        {showAssessmentButton && projectStatus !== 'finished' && disableAssessmentButton && (
          <Tooltip label={t('new_project_view.tooltip_lesson_no_assessments')} withinPortal withArrow position='top'>
            <Button
              data-disabled
              size='sm'
              leftIcon={<IconClipboardCheck size={16} />}
              sx={{ '&[data-disabled]': { pointerEvents: 'all' } }}
              data-testid='assess_students-disabled'
              onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
            >
              {assessmentLabel}
            </Button>
          </Tooltip>
        )}

        {showAssessmentButton && projectStatus !== 'finished' && !disableAssessmentButton && canFillEditReport && (
          opensInDrawer ? (
            <Button
              variant='filled'
              size='sm'
              leftIcon={<IconClipboardCheck size={16} />}
              onClick={handleAssessmentClick}
            >
              {assessmentLabel}
            </Button>
          ) : (
            <NavigationButton
              variant='filled'
              size='sm'
              leftIcon={<IconClipboardCheck size={16} />}
              url={getJournalURL(lessonID)}
            >
              {assessmentLabel}
            </NavigationButton>
          )
        )}

        {showAssessmentButton && projectStatus !== 'finished' && !disableAssessmentButton && !canFillEditReport && (
          <Tooltip label={t('fill_report_disabled')} withinPortal withArrow position='top'>
            <Button
              data-disabled
              size='sm'
              leftIcon={<IconClipboardCheck size={16} />}
              sx={{ '&[data-disabled]': { pointerEvents: 'all' } }}
              data-testid='fill_report-disabled'
              onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
            >
              {assessmentLabel}
            </Button>
          </Tooltip>
        )}
        {showDeliverableButton && (
          <Button
            variant='filled'
            size='sm'
            leftIcon={<IconUpload size={16} />}
            onClick={() => setDeliverableDrawerOpened(true)}
          >
            {t('task_tracker.deliverable_drawer.submit_button_card')}
          </Button>
        )}

        {showReportButton && (
          <Button
            variant='outline'
            size='sm'
            leftIcon={<IconFileText size={16} />}
            component='a'
            href={getReportURL(lessonID)}
          >
            {reportButtonLabel}
          </Button>
        )}
      </Group>

      {showDeliverableButton && deliverableDrawerOpened && (
        <StudentDeliverableDrawer
          opened={deliverableDrawerOpened}
          activityIds={deliverableActivityIds}
          onClose={() => setDeliverableDrawerOpened(false)}
        />
      )}

      {openStudentDrawer && (
        <TaskTrackerAssessmentDrawer
          opened={studentDrawerOpened}
          sessionMeetingId={lessonID}
          studentId={currentUser?.profile_url?.split('/')[2] ?? currentUser?.id ?? ''}
          assessmentId={null}
          lessonTitle={lesson?.lessonName ?? lesson?.title}
          lessonDate={lesson?.date}
          activities={lesson?.sessionMeetingActivities}
          onClose={() => setStudentDrawerOpened(false)}
        />
      )}

      {openTeacherDrawer && (
        <TeacherAssessmentDrawer
          opened={teacherDrawerOpened}
          sessionMeetingId={lessonID}
          defaultAllStudents
          onClose={() => setTeacherDrawerOpened(false)}
        />
      )}
    </>
  )
}
